/**
 * HTML to Image Capture Utilities
 *
 * Captures HTML artefacts (rendered in iframes) as high-quality images
 * for embedding in batch exports (PowerPoint, Word, PDF)
 */

import html2canvas from "html2canvas";

import { Artefact } from "@/types/artefacts";
import { logger } from "@/telemetry";
import { escapeCssSelector, sanitizeUnsupportedColors } from "@/utils/styleUtils";

// Image capture configuration constants
const IMAGE_CAPTURE_SCALE = 2; // High DPI for crisp images (2x device pixel ratio)
const CHART_RENDER_DELAY_MS = 2000; // Final delay to ensure animations/transitions complete
const CHART_POLLING_INTERVAL_MS = 100; // Polling interval for checking chart rendering
const CHART_DETECTION_DELAY_MS = 200; // Additional delay after canvas/SVG detection

// Document dimension constants for calculating optimal capture widths
// A4: 210mm width, Letter: 216mm width
// With 25mm margins on each side, content area is ~160mm for A4
// At 96 DPI (screen resolution): 160mm ≈ 605px
// At 2x capture scale, we capture at 605px then scale down for crisp images

// Default capture widths for different export targets (in pixels)
// These are based on typical document content widths to ensure proper fit
export const CAPTURE_WIDTH_WORD = 600; // ~160mm content width for A4/Letter documents
export const CAPTURE_WIDTH_PDF = 600; // ~160mm content width for A4/Letter documents
export const CAPTURE_WIDTH_POWERPOINT = 1200; // Wider for 16:9 slides (25.4cm presentation width)
export const CAPTURE_WIDTH_DEFAULT = 600; // Default to document-friendly width

/**
 * Calculate optimal capture width based on page dimensions
 * @param pageWidthMm - Page width in millimetres (e.g., 210 for A4)
 * @param marginMm - Margin on each side in millimetres (default: 25mm)
 * @param dpi - Target DPI for the capture (default: 96 for screen)
 * @returns Optimal capture width in pixels
 */
export function calculateCaptureWidth(
  pageWidthMm: number,
  marginMm: number = 25,
  dpi: number = 96,
): number {
  const contentWidthMm = pageWidthMm - 2 * marginMm;
  // Convert mm to inches, then to pixels at target DPI
  const contentWidthInches = contentWidthMm / 25.4;
  const contentWidthPx = Math.round(contentWidthInches * dpi);

  return contentWidthPx;
}

// Pre-calculated widths for common page sizes (at 96 DPI with 25mm margins)
export const PAGE_CAPTURE_WIDTHS = {
  A4: calculateCaptureWidth(210, 25), // ~605px
  Letter: calculateCaptureWidth(216, 25), // ~628px
  Legal: calculateCaptureWidth(216, 25), // ~628px (same width as Letter)
};

/**
 * Options for HTML artefact image capture
 */
export interface CaptureOptions {
  /** Width in pixels for the capture viewport (default: 800) */
  width?: number;
}

export interface CapturedImage {
  artefactId: string;
  base64: string; // Base64-encoded PNG
  imageData: Uint8Array; // Binary image data for docx
  width: number;
  height: number;
}

/**
 * Capture a single HTML artefact as a high-quality image
 *
 * @param artefact - The HTML artefact to capture
 * @returns Promise with captured image data in multiple formats
 * @throws Error if iframe not found or capture fails
 */
export async function captureHtmlArtefactAsImage(artefact: Artefact): Promise<CapturedImage> {
  // Find the iframe element by artefact ID
  const escapedId = escapeCssSelector(artefact.id);
  const iframe = document.querySelector(
    `iframe[data-artefact-id="${escapedId}"]`,
  ) as HTMLIFrameElement;

  if (!iframe) {
    logger.error(`Iframe not found for artefact: ${artefact.title}`, undefined, {
      escapedId,
      allIframes: Array.from(document.querySelectorAll("iframe"))
        .map((el) => el.getAttribute("data-artefact-id"))
        .filter(Boolean)
        .join(", "),
    });
    throw new Error(
      `Iframe not found for artefact: ${artefact.title}. Please ensure the chart is visible in the Generated Content panel before exporting.`,
    );
  }

  if (!iframe.contentDocument) {
    throw new Error(`Cannot access iframe content for artefact: ${artefact.title}`);
  }

  // Capture the iframe content as canvas with high quality
  const canvas = await html2canvas(iframe.contentDocument.body, {
    scale: IMAGE_CAPTURE_SCALE,
    backgroundColor: "#ffffff", // White background for better print quality
    logging: false,
    useCORS: true,
    allowTaint: false,
    removeContainer: true,
    imageTimeout: 15000,
    windowWidth: iframe.contentDocument.body.scrollWidth,
    windowHeight: iframe.contentDocument.body.scrollHeight,
    // Sanitize unsupported CSS colour functions before capture
    // html2canvas doesn't support modern colour spaces like oklab(), oklch()
    onclone: (clonedDoc: globalThis.Document) => {
      sanitizeUnsupportedColors(clonedDoc);
    },
  });

  // Convert to base64 with maximum quality (no compression)
  const base64 = canvas.toDataURL("image/png", 1.0);

  // Convert to Uint8Array for Word (docx library) with maximum quality
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (result) => {
        if (result) {
          resolve(result);
        } else {
          reject(new Error("Failed to convert canvas to blob"));
        }
      },
      "image/png",
      1.0, // Maximum quality
    );
  });

  const arrayBuffer = await blob.arrayBuffer();
  const imageData = new Uint8Array(arrayBuffer);

  return {
    artefactId: artefact.id,
    base64,
    imageData,
    width: canvas.width,
    height: canvas.height,
  };
}

/**
 * Temporarily create hidden iframes for HTML artefacts that aren't visible
 *
 * @param artefacts - Array of HTML artefacts
 * @param captureWidth - Width in pixels for the capture viewport (default: CAPTURE_WIDTH_DEFAULT)
 * @returns Map of artefact IDs to temporary iframes
 */
function createTemporaryIframes(
  artefacts: Artefact[],
  captureWidth: number = CAPTURE_WIDTH_DEFAULT,
): Map<string, HTMLIFrameElement> {
  const tempIframes = new Map<string, HTMLIFrameElement>();

  artefacts.forEach((artefact) => {
    const escapedId = escapeCssSelector(artefact.id);
    const existingIframe = document.querySelector(
      `iframe[data-artefact-id="${escapedId}"]`,
    ) as HTMLIFrameElement;

    // Only create temporary iframe if one doesn't exist
    if (!existingIframe) {
      const iframe = document.createElement("iframe");

      iframe.setAttribute("data-artefact-id", artefact.id);
      iframe.setAttribute("data-temporary", "true");
      iframe.style.position = "fixed";
      iframe.style.left = "-99999px"; // Far off-screen - hidden from user but still rendered
      iframe.style.top = "0";
      iframe.style.width = `${captureWidth}px`; // Configurable width for different export targets
      iframe.style.height = "10000px"; // Very tall to ensure all content is visible
      iframe.style.border = "none";
      // IMPORTANT: Do NOT use visibility:hidden or display:none
      // html2canvas cannot capture elements that are not rendered/visible
      // Off-screen positioning is sufficient to hide from user while allowing capture
      iframe.sandbox.add("allow-scripts", "allow-same-origin");

      // Create HTML content with proper viewport for charts
      // Check if content is already a complete HTML document (with Chart.js, etc.)
      let htmlContent: string;

      if (artefact.content.includes("<html")) {
        // Content is already a complete HTML document - ensure proper rendering at target width
        htmlContent = artefact.content
          // Update viewport meta tag
          .replace(
            /<meta\s+name="viewport"\s+content="[^"]*">/i,
            `<meta name="viewport" content="width=${captureWidth}, initial-scale=1.0">`,
          )
          // Inject style to force body and container to target width with natural height
          .replace(
            "</head>",
            `<style>
              html, body {
                min-width: ${captureWidth}px !important;
                width: ${captureWidth}px !important;
                height: auto !important;
                min-height: 100% !important;
                max-height: none !important;
                overflow: visible !important;
              }
              body > * {
                max-width: ${captureWidth}px !important;
                width: ${captureWidth}px !important;
                height: auto !important;
                max-height: none !important;
              }
              canvas, svg {
                width: 100% !important;
                height: auto !important;
                max-width: ${captureWidth}px !important;
              }
              /* Mermaid diagram specific styles */
              .mermaid, [id^="mermaid-"] {
                width: ${captureWidth}px !important;
                max-width: ${captureWidth}px !important;
                height: auto !important;
              }
              .mermaid svg, [id^="mermaid-"] svg {
                width: ${captureWidth}px !important;
                max-width: ${captureWidth}px !important;
                height: auto !important;
              }
            </style>
            </head>`,
          );
      } else {
        // Content is a fragment - wrap in HTML document
        htmlContent = `
          <!DOCTYPE html>
          <html lang="en">
            <head>
              <meta charset="UTF-8">
              <meta name="viewport" content="width=${captureWidth}, initial-scale=1.0">
              <title>${artefact.title}</title>
              <style>
                body {
                  margin: 0;
                  padding: 20px;
                  width: ${captureWidth}px;
                  min-height: 600px;
                  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                  background: white;
                  color: #1a1a1a;
                }
                canvas {
                  max-width: 100%;
                  height: auto;
                }
              </style>
            </head>
            <body>
              ${artefact.content}
            </body>
          </html>
        `;
      }

      iframe.srcdoc = htmlContent;
      document.body.appendChild(iframe);
      tempIframes.set(artefact.id, iframe);
    }
  });

  return tempIframes;
}

/**
 * Remove temporary iframes created for capture
 *
 * @param tempIframes - Map of temporary iframes to remove
 */
function removeTemporaryIframes(tempIframes: Map<string, HTMLIFrameElement>): void {
  tempIframes.forEach((iframe) => {
    if (iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
  });
}

/**
 * Wait for iframes to finish loading and content to render
 *
 * @param tempIframes - Map of temporary iframes
 * @param timeout - Maximum time to wait in milliseconds
 */
async function waitForIframesToLoad(
  tempIframes: Map<string, HTMLIFrameElement>,
  timeout: number = 10000,
): Promise<void> {
  const loadPromises = Array.from(tempIframes.values()).map(
    (iframe) =>
      new Promise<void>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error("Iframe load timeout")), timeout);

        const checkIfLoaded = () => {
          if (iframe.contentDocument?.readyState === "complete") {
            clearTimeout(timer);
            resolve();
          }
        };

        // Check immediately
        if (iframe.contentDocument?.readyState === "complete") {
          clearTimeout(timer);
          resolve();
        } else {
          // Wait for load event
          iframe.addEventListener("load", checkIfLoaded);

          // Also check DOMContentLoaded in the iframe
          iframe.contentWindow?.addEventListener("DOMContentLoaded", checkIfLoaded);
        }
      }),
  );

  await Promise.all(loadPromises);
}

/**
 * Wait for canvas elements to appear in iframes (indicates chart rendering)
 *
 * @param tempIframes - Map of temporary iframes
 * @param maxWaitTime - Maximum time to wait in milliseconds
 */
async function waitForChartRendering(
  tempIframes: Map<string, HTMLIFrameElement>,
  maxWaitTime: number = 3000,
): Promise<void> {
  const startTime = Date.now();

  const checkPromises = Array.from(tempIframes.values()).map(async (iframe) => {
    while (Date.now() - startTime < maxWaitTime) {
      const canvas = iframe.contentDocument?.querySelector("canvas");
      const svg = iframe.contentDocument?.querySelector("svg");

      // If we find a canvas or SVG element, chart is likely rendered
      if (canvas || svg) {
        // Extra small delay to ensure rendering is complete
        await new Promise((resolve) => setTimeout(resolve, CHART_DETECTION_DELAY_MS));

        return;
      }

      // Check again after polling interval
      await new Promise((resolve) => setTimeout(resolve, CHART_POLLING_INTERVAL_MS));
    }
  });

  await Promise.all(checkPromises);
}

/**
 * Capture multiple HTML artefacts as images in parallel
 *
 * @param artefacts - Array of HTML artefacts to capture
 * @param options - Optional capture configuration (width, etc.)
 * @returns Promise with array of captured images
 * @throws Error if any capture fails
 */
export async function captureHtmlArtefactsAsImages(
  artefacts: Artefact[],
  options: CaptureOptions = {},
): Promise<CapturedImage[]> {
  const captureWidth = options.width ?? CAPTURE_WIDTH_DEFAULT;

  // Create temporary hidden iframes for artefacts that aren't visible
  const tempIframes = createTemporaryIframes(artefacts, captureWidth);

  try {
    // Wait for temporary iframes to load
    if (tempIframes.size > 0) {
      logger.info(`Waiting for ${tempIframes.size} iframes to load...`);
      await waitForIframesToLoad(tempIframes);

      logger.info("Iframes loaded, waiting for chart rendering...");
      // Wait for canvas/SVG elements to appear (chart libraries)
      await waitForChartRendering(tempIframes);

      logger.info("Charts rendered, adding final delay...");
      // Additional delay to ensure all animations/transitions complete
      await new Promise((resolve) => setTimeout(resolve, CHART_RENDER_DELAY_MS));

      logger.info("Ready to capture charts!");
    }

    // Capture all HTML artefacts in parallel for better performance
    const capturePromises = artefacts.map((artefact) => captureHtmlArtefactAsImage(artefact));

    return await Promise.all(capturePromises);
  } finally {
    // Clean up temporary iframes
    removeTemporaryIframes(tempIframes);
  }
}

/**
 * Create a Map of artefact ID to captured image for easy lookup
 *
 * @param images - Array of captured images
 * @returns Map with artefact IDs as keys and images as values
 */
export function createImageMap(images: CapturedImage[]): Map<string, CapturedImage> {
  return new Map(images.map((img) => [img.artefactId, img]));
}
